<template>
  <v-card elevation="0">
    <invoice-verification-dialog
      v-bind="verifiedInvoiceDialog"
      @load-invoice="loadInvoice"
      @dismiss="hideInvoiceVerifyDialog"
    />
    <invoice-creation-dialog
      v-bind="newInvoiceDialog"
      @create-invoice="createInvoice"
      @dismiss="hideInvoiceCreateDialog"
    />
    <alert-card
        v-if="alert.visible === true"
        v-model:visible="alert.visible"
        :type="alert.type"
        :title="alert.title"
        :text="alert.text"
        :closable="false"
        class="px-0"
    />
    <v-row
        class="px-4"
        no-gutters="true"
        justify="end"
    >
      <v-col cols="12" md="2">
        <v-btn
            color="orange"
            class="mr-md-3 mb-3"
            text="Verify invoice"
            prepend-icon="mdi-check"
            @click="showInvoiceVerifyDialog"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-btn
            color="success"
            text="Create invoice"
            prepend-icon="mdi-receipt"
            @click="showInvoiceCreateDialog"
        />
      </v-col>
    </v-row>
    <invoice-list
        v-model:items="invoiceList.items"
        v-model:is-loading="invoiceList.isLoading"
        :skeleton-amount="invoiceList.limit"
        @download-invoice="downloadInvoice"
    />
    <v-pagination
        v-model="this.pagination.currentPage"
        :length="this.pagination.totalPages"
    />
  </v-card>
</template>

<script>
import AlertCard from "@/components/AlertCard.vue";
import {inject} from "vue";
import {auth} from "@/common/auth";
import {router} from "@/router";
import {api} from "@/common/api";
import {messages} from "@/common/enum/messages";
import {dates} from "@/common/dates";
import InvoiceList from "@/components/Invoice/InvoiceList.vue";
import InvoiceVerificationDialog from "@/components/Invoice/InvoiceVerificationDialog.vue";
import InvoiceCreationDialog from "@/components/Invoice/InvoiceCreationDialog.vue";
import {numbers} from "@/common/numbers";

export default {
  name: "UserInvoicesView",
  components: {InvoiceCreationDialog, InvoiceVerificationDialog, InvoiceList, AlertCard},
  data() {
    return {
      alert: {
        visible: false,
        type: 'error',
        title: 'Error',
        text: null
      },
      invoiceList: {
        isLoading: false,
        request: null,
        items: [],
        page: 1,
        limit: 10,
      },
      pagination: {
        currentPage: 1,
        limit: 10,
        totalPages: null,
      },
      verifiedInvoiceDialog: {
        isLoading: false,
        isVisible: false,
        request: null,
        error: {
          isVisible: false,
          message: null
        },
        invoice: {
          fullname: null,
          address: null,
          manufacturer: null,
          terms: null,
          destination: null,
          currency: null,
          items: [],
          bank: null,
          bankAddress: null,
          bankAccountNumber: null,
          bankSwiftCode: null,
          totalQuantity: null,
          totalPrice: null
        }
      },
      newInvoiceDialog: {
        isLoading: false,
        isVisible: false,
        request: null,
        error: {
          isVisible: false,
          message: null
        },
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.loadInvoiceList()
    },
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  mounted() {
    this.loadInvoiceList()
  },
  methods: {
    async getToken() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        await router.push({name: 'sign-in', state: {redirectTo: 'profile'}})
        return
      }
      return token
    },
    async downloadInvoice(id) {
      const token = await this.getToken()
      let response = await api.downloadFile(token, id)
      this.handleDownloadInvoiceResponse(response)
    },
    handleDownloadInvoiceResponse(response) {
      if (this.verifyError(response)) {
        this.showError(response.data?.message, messages.en.user_invoices_view.file_loading_error)
      } else {
        this.hideError()
        this.triggerDownloadFile(response)
      }
    },
    verifyError(response) {
      return response?.data?.status === 'error'
    },
    hideError() {
      this.alert.visible = false
    },
    showError(message, defaultMessage) {
      this.alert.visible = true
      this.alert.type = 'error'
      this.alert.title = 'Error'
      this.alert.text = message?.length === 0 ? defaultMessage : message
    },
    triggerDownloadFile(response) {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers.filename);
      document.body.appendChild(link);
      link.click();
    },
    async loadInvoiceList() {
      const token = await this.getToken()
      this.verifyNoRequest(this.invoiceList.request)
      this.invoiceList.request = new AbortController()
      this.invoiceList.isLoading = true
      let response = await api.getInvoices(token, {
        page: this.pagination.currentPage,
        limit: this.pagination.limit
      }, this.invoiceList.request.signal)
      this.handleGetInvoicesResponse(response)
    },
    verifyNoRequest(request) {
      if (request && !request.signal.aborted) {
        request.abort()
      }
    },
    handleGetInvoicesResponse(response) {
      if (this.verifyError(response)) {
        this.showError(response.data?.message, messages.en.user_invoices_view.invoices_loading_error)
      } else {
        this.hideError()
        this.parseGetInvoicesResponse(response)
      }
      this.invoiceList.isLoading = false
    },
    parseGetInvoicesResponse(response) {
      const invoices = response.data.invoices
      this.pagination.totalPages = Math.ceil(response.data.total / this.pagination.limit)
      this.invoiceList.items = invoices.map(invoice => {
        return {
          id: invoice.file.id,
          title: invoice.file.file_name,
          size: invoice.file.size,
          createdAt: dates.mapToDate(invoice.created_at)
        }
      })
    },
    async loadInvoice(id) {
      const token = await this.getToken()
      this.verifyNoRequest(this.verifiedInvoiceDialog.request)
      this.verifiedInvoiceDialog.request = new AbortController()
      this.verifiedInvoiceDialog.isLoading = true
      let response = await api.getInvoice(
          token,
          id,
          this.verifiedInvoiceDialog.request.signal
      )
      this.handleGetInvoiceResponse(response)
    },
    handleGetInvoiceResponse(response) {
      if (this.verifyError(response)) {
        this.showInvoiceVerifyError(response.data?.message, messages.en.user_invoices_view.invoice_loading_error)
      } else {
        this.hideInvoiceVerifyError()
        this.parseGetInvoiceResponse(response)
      }
      this.verifiedInvoiceDialog.isLoading = false
    },
    showInvoiceVerifyError(message, defaultMessage) {
      this.verifiedInvoiceDialog.error.isVisible = true
      this.verifiedInvoiceDialog.error.message = message?.length === 0 ? defaultMessage : message
    },
    hideInvoiceVerifyError() {
      this.verifiedInvoiceDialog.error.isVisible = false
    },
    parseGetInvoiceResponse(response) {
      const invoice = response.data
      this.verifiedInvoiceDialog.invoice = {
        fullname: invoice.fullname,
        address: invoice.address,
        manufacturer: invoice.manufacturer,
        terms: invoice.terms,
        destination: invoice.destination,
        currency: invoice.currency,
        items: invoice.items.map(item => {
          return {
            description: item.description,
            model: item.model,
            quantity: item.quantity,
            price: numbers.formatMoney(item.price, invoice.currency),
            totalPrice: numbers.formatMoney(item.quantity * item.price, invoice.currency),
            totalPriceValue: item.quantity * item.price
          }
        }),
        totalQuantity: invoice.items.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: numbers.formatMoney(invoice.items.reduce((sum, item) => sum + item.price * item.quantity, 0), invoice.currency),
        bank: invoice.bank,
        bankAddress: invoice.bank_address,
        bankAccountNumber: invoice.bank_account,
        bankSwiftCode: invoice.bank_swift_code
      }
    },
    async createInvoice(invoice) {
      if (invoice.items.value.length === 0) {
        this.showInvoiceCreateError(messages.en.user_invoices_view.invoice_creation_error)
        return
      }

      const token = await this.getToken()
      this.verifyNoRequest(this.newInvoiceDialog.request)

      this.newInvoiceDialog.request = new AbortController()
      this.newInvoiceDialog.isLoading = true
      let response = await api.createInvoice(
          token,
          {
            fullname: invoice.fullname.value,
            address: invoice.address.value,
            manufacturer: invoice.manufacturer.value,
            terms: invoice.terms.value,
            bank: invoice.bank.value,
            currency: invoice.currency.value,
            destination: invoice.destination.value,
            items: invoice.items.value
          },
          this.newInvoiceDialog.request.signal,
      )
      this.handleCreateInvoiceResponse(response)
    },
    showInvoiceCreateError(message, defaultMessage) {
      this.newInvoiceDialog.error.isVisible = true
      this.newInvoiceDialog.error.message = message?.length === 0 ? defaultMessage : message
    },
    handleCreateInvoiceResponse(response) {
      if (this.verifyError(response)) {
        this.showInvoiceCreateError(response.data?.message, messages.en.user_invoices_view.invoice_creation_error)
      } else {
        this.hideInvoiceCreateError()
        this.page = 1
      }
      this.newInvoiceDialog.isLoading = false
      this.hideInvoiceCreateDialog()
      this.loadInvoiceList()
    },
    hideInvoiceCreateError() {
      this.newInvoiceDialog.error.isVisible = false
    },
    showInvoiceCreateDialog() {
      this.newInvoiceDialog.isVisible = true
      this.verifiedInvoiceDialog.isVisible = false
    },
    hideInvoiceCreateDialog() {
      this.newInvoiceDialog.isVisible = false
      this.newInvoiceDialog.error.isVisible = false
    },
    showInvoiceVerifyDialog() {
      this.newInvoiceDialog.isVisible = false
      this.verifiedInvoiceDialog.isVisible = true
    },
    hideInvoiceVerifyDialog() {
      this.verifiedInvoiceDialog.isVisible = false
      this.verifiedInvoiceDialog.error.isVisible = false
      this.verifiedInvoiceDialog.invoice = {
        fullname: null,
        address: null,
        manufacturer: null,
        terms: null,
        destination: null,
        currency: null,
        items: [],
        bank: null,
        bankAddress: null,
        bankAccountNumber: null,
        bankSwiftCode: null,
        totalQuantity: null,
        totalPrice: null
      }
    }
  }
}
</script>

<style scoped>

</style>